import type { FeatureRow } from 'features/customers/utils/featureRows'

// What the server reads as "stop storing this key". A switch and a select cannot express it, and for
// an override holding the plan's own value it is the only way out.
export const REMOVE_OVERRIDE = null

export type FeatureEdits = Record<string, unknown>

export type PreparedEdits = {
  payload: FeatureEdits
  invalid: string[]
}

// Trimmed: the field is text, so spaces look empty while Number('   ') is 0 - a real zero cap.
const isBlank = (value: unknown) =>
  value === null || value === undefined || (typeof value === 'string' && !value.trim())

export const isUnchanged = (row: FeatureRow, value: unknown) => {
  // Before the kinds: a select's empty option is None, a value someone chose, and not a removal.
  if (value === REMOVE_OVERRIDE) return !row.overridden

  if (row.kind === 'flag') return !!value === !!row.value

  // Blank-normalised: an unset provider is undefined and None sends '', which is the same state.
  if (row.kind === 'text') return (isBlank(value) ? '' : value) === (isBlank(row.value) ? '' : row.value)

  // Against the displayed value, not the override: the field shows the plan's number when nothing
  // is stored, so clearing it is a change on screen even when it saves nothing.
  if (isBlank(value)) return isBlank(row.value)

  return Number(value) === Number(row.value)
}

export const prepareFeatureEdits = (edits: FeatureEdits, rows: FeatureRow[]): PreparedEdits => {
  const byName = new Map(rows.map(row => [row.name, row]))

  const payload: FeatureEdits = {}
  const invalid: string[] = []

  for (const [name, value] of Object.entries(edits)) {
    const row = byName.get(name)

    if (!row) continue

    if (value === REMOVE_OVERRIDE) {
      payload[name] = REMOVE_OVERRIDE
      continue
    }

    if (row.kind === 'flag') {
      payload[name] = !!value
      continue
    }

    if (row.kind === 'text') {
      // Normalised as isUnchanged does, or the two disagree about whitespace.
      payload[name] = isBlank(value) ? '' : value
      continue
    }

    if (isBlank(value)) {
      payload[name] = REMOVE_OVERRIDE
      continue
    }

    const asNumber = Number(value)

    // Nothing server-side rejects these (DEV-12423). Zero stays legal: the api plan sets it.
    if (!Number.isInteger(asNumber) || asNumber < 0) invalid.push(name)
    else payload[name] = asNumber
  }

  return { payload, invalid }
}

export const hasEdits = (edits: FeatureEdits) => Object.keys(edits).length > 0
